import { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { PresentUrl, denaveAadharFetchApi, idVerificationFormApi, panVerifyApi } from "../../../text/apidata.js";
import ImageCropper from "../ReactcropOverall/Imagecrop.js";
import { store } from "../../../Redux/Store.js";
import { responsiveToast } from "../../AppComponent/responsiveToast.js";
import {
  selectEmployeeInformation,
  updateIdField,
  setIdImage,
  removeIdImage,
  setAddressImage,
} from "../../../Redux/employeeInformationSlice.js";
import { getLoaderAction2 } from "../../../Redux/Loader2.js";
import styles from "./IdVerification.module.scss";

const formatDateToDDMMYYYY = (dobStr) => {
  if (!dobStr) return "";
  let str = String(dobStr).trim();
  if (str.includes("T")) {
    str = str.split("T")[0];
  }
  if (str.includes("/")) {
    const parts = str.split("/");
    if (parts.length === 3) {
      if (parts[2].length === 4) {
        // DD/MM/YYYY
        return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
      } else if (parts[0].length === 4) {
        // YYYY/MM/DD -> DD/MM/YYYY
        return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
      }
    }
  }
  if (str.includes("-")) {
    const parts = str.split("-");
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD -> DD/MM/YYYY
        return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
      } else if (parts[2].length === 4) {
        // DD-MM-YYYY -> DD/MM/YYYY
        return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
      }
    }
  }
  return str;
};

const IdVerification = forwardRef(function IdVerification(_, ref) {
  const dispatch = useDispatch();

  const {
    idVerification,
    basicInformation,
    basicImages,
    addressImages,
    idVerificationImages,
  } = useSelector(selectEmployeeInformation);

  const selfonboardData = useSelector(
    (state) => state.selfonboardingretriveslice?.Selfonboarding_Data
  );
  const employeeDetails = useSelector(
    (state) => state.Signature_Employee_RetrieveCall_actions?.Signature_Employee_Datas
  );

  const [errorFields, setErrorFields] = useState(new Set());
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperTargetName, setCropperTargetName] = useState("");
  const [selectedImageEvent, setSelectedImageEvent] = useState(null);
  const [isPanVerified, setIsPanVerified] = useState(false);
  const [isVerifyingPan, setIsVerifyingPan] = useState(false);
  const [extractedPanDob, setExtractedPanDob] = useState("");
  const [extractedPanName, setExtractedPanName] = useState("");

  const fetchedIdFormIdRef = useRef(null);

  const extractIdFormId = (d) => {
    if (!d) return null;
    const raw =
      d?.employeetimeline_id?.id_verification ||
      d?.employeetimeline_id?.id_verification_form ||
      d?.employeetimeline_id?.id_details_form ||
      d?.employeetimeline_id?.id_details ||
      d?.id_details_form?.id ||
      d?.id_details_form ||
      d?.id_verification?.id ||
      d?.id_verification ||
      d?.id_verification_id ||
      d?.id_details ||
      d?.idform_id ||
      d?.id_details_form_id;

    if (typeof raw === "object" && raw !== null) {
      return raw.id || raw.id_details_form || raw.id_verification || raw._id || null;
    }
    return raw ? String(raw) : null;
  };

  // GET ID Verification Details API call on mount
  useEffect(() => {
    const details = selfonboardData || employeeDetails;
    if (!details) {
      dispatch(getLoaderAction2(false));
      return;
    }

    const idFormId = extractIdFormId(details);
    if (!idFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }

    if (fetchedIdFormIdRef.current === idFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }
    fetchedIdFormIdRef.current = idFormId;

    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));

    axios
      .get(`${idVerificationFormApi}${idFormId}/`, { headers })
      .then((res) => {
        console.log("ID Verification GET API response:", res.data);
        const data = res.data?.data || res.data;
        if (data) {
          if (data.pan_number) {
            dispatch(updateIdField({ name: "pan_number", value: String(data.pan_number).toUpperCase() }));
          }

          const isVerifiedOnServer =
            data.pan_verified === true ||
            data.is_pan_verified === true ||
            data.pan_verified === "true";

          if (isVerifiedOnServer) {
            setIsPanVerified(true);
            dispatch(updateIdField({ name: "pan_verified", value: true }));
          } else {
            setIsPanVerified(false);
            dispatch(updateIdField({ name: "pan_verified", value: false }));
          }

          const panObj =
            data?.pan ||
            data?.pan_card ||
            data?.pan_id ||
            data?.pancard_id ||
            data?.pan_card_id;

          const panUrl =
            (typeof panObj === "object" && panObj !== null
              ? panObj?.media_url || panObj?.url
              : typeof panObj === "string"
                ? panObj
                : "") || "";

          if (panUrl) {
            dispatch(
              setIdImage({
                name: "pan",
                image: {
                  name: "PAN Card",
                  url: panUrl,
                  s3Url: panUrl,
                },
              })
            );
            dispatch(updateIdField({ name: "pan_id", value: panUrl }));
            dispatch(updateIdField({ name: "pan", value: panUrl }));
          }

          const aadharBackObj =
            data?.aadharback_id ||
            data?.aadhar_back ||
            data?.aadhar_back_id ||
            data?.aadhaar_back ||
            data?.aadhaarback_id ||
            data?.aadhar_id;

          const aadharBackUrl =
            (typeof aadharBackObj === "object" && aadharBackObj !== null
              ? aadharBackObj?.media_url || aadharBackObj?.url
              : typeof aadharBackObj === "string"
                ? aadharBackObj
                : "") || "";

          if (aadharBackUrl) {
            const imgData = {
              name: "Aadhaar Back",
              url: aadharBackUrl,
              s3Url: aadharBackUrl,
            };
            dispatch(setIdImage({ name: "aadhaarBack", image: imgData }));
            dispatch(setAddressImage({ name: "aadhaarBack", image: imgData }));
          }

          const aadharFrontObj =
            data?.aadharfront_id ||
            data?.aadhar_front ||
            data?.aadhar_front_id ||
            data?.aadhaar_front ||
            data?.aadhaarfront_id;

          const aadharFrontUrl =
            (typeof aadharFrontObj === "object" && aadharFrontObj !== null
              ? aadharFrontObj?.media_url || aadharFrontObj?.url
              : typeof aadharFrontObj === "string"
                ? aadharFrontUrl
                : "") || "";

          if (aadharFrontUrl) {
            const imgData = {
              name: "Aadhaar Front",
              url: aadharFrontUrl,
              s3Url: aadharFrontUrl,
            };
            dispatch(setIdImage({ name: "aadhaarFront", image: imgData }));
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching ID Verification GET API:", err);
      })
      .finally(() => {
        dispatch(getLoaderAction2(false));
      });
  }, [dispatch, selfonboardData, employeeDetails]);

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateIdField({ name, value }));
    clearError(name);
  };

  const handleVerifyPan = async () => {
    const panVal = String(idVerification.pan_number || "").trim().toUpperCase();
    if (panVal.length !== 10) return;

    setIsVerifyingPan(true);
    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    const rawDob =
      extractedPanDob ||
      basicInformation.DateofBirth ||
      basicInformation.date_of_birth ||
      basicInformation.dob ||
      basicInformation.date_of_birth_match ||
      selfonboardData?.basicform_id?.date_of_birth ||
      selfonboardData?.basic_form?.date_of_birth ||
      selfonboardData?.date_of_birth ||
      selfonboardData?.dob ||
      employeeDetails?.basicform_id?.date_of_birth ||
      employeeDetails?.date_of_birth ||
      employeeDetails?.dob ||
      "";

    const formattedDob = formatDateToDDMMYYYY(rawDob);

    const nameAsPerPan = String(
      extractedPanName ||
      basicInformation.Name ||
      basicInformation.name ||
      basicInformation.first_name ||
      selfonboardData?.basicform_id?.name ||
      selfonboardData?.name ||
      employeeDetails?.name ||
      ""
    ).trim().toUpperCase();

    try {
      dispatch(getLoaderAction2(true));
      const payload = {
        // name_as_per_pan: nameAsPerPan,
        // date_of_birth: formattedDob,
        panNumber: panVal,
      };

      console.log("Submitting PAN verify payload:", payload);
      const res = await axios.post(panVerifyApi, payload, { headers });
      console.log("PAN Verify API response:", res.data);

      const innerData = res.data?.data || {};
      const innerCode = innerData?.code;
      const innerStatus = innerData?.status;
      const innerMessage = innerData?.message;
      const topStatusMessage = res.data?.status?.message;

      // Validate inner data.code (must be 200)
      if (innerCode === 200 || innerStatus === "valid") {
        setIsPanVerified(true);
        dispatch(updateIdField({ name: "pan_verified", value: true }));
        dispatch(updateIdField({ name: "is_id_verification", value: true }));
        dispatch(updateIdField({ name: "aadhar_pan_linked", value: innerData?.aadhaar_seeding_status === "y" }));
        responsiveToast(topStatusMessage || innerMessage || "PAN verified successfully", "success");
      } else {
        const errorMsg = innerMessage || res.data?.message || res.data?.detail || "PAN verification failed";
        setIsPanVerified(false);
        dispatch(updateIdField({ name: "pan_verified", value: false }));
        dispatch(updateIdField({ name: "is_id_verification", value: false }));
        responsiveToast(errorMsg, "error");
      }
    } catch (err) {
      console.error("PAN Verification error:", err);
      const errorMsg =
        err.response?.data?.data?.message ||
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "PAN verification failed";
      setIsPanVerified(false);
      responsiveToast(errorMsg, "error");
    } finally {
      setIsVerifyingPan(false);
      dispatch(getLoaderAction2(false));
    }
  };

  const handleFileInputChange = (name, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      responsiveToast("File size exceeds 10MB", "error");
      return;
    }

    setSelectedImageEvent(event);
    setCropperTargetName(name);
    setCropperOpen(true);
  };

  const getCroppedImg = (imageRef, crop, fileName) => {
    const canvas = document.createElement("canvas");
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], fileName, {
          type: "image/jpeg",
        });

        setCropperOpen(false);
        handleImageChange(cropperTargetName, croppedFile);
        resolve(croppedFile);
      }, "image/jpeg");
    });
  };

  const handleImageChange = async (name, file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (idVerificationImages[name]?.url) {
      URL.revokeObjectURL(idVerificationImages[name].url);
    }

    const localUrl = URL.createObjectURL(file);

    dispatch(
      setIdImage({
        name,
        image: {
          name: file.name,
          url: localUrl,
          s3Url: "",
          file: file,
        },
      })
    );

    if (name === "pan") {
      updateField("pan_id", localUrl);
    }

    clearError(name);

    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));
    try {
      // 1. Presigned S3 Upload
      const folderName = "pancard";
      const randomNumber = Math.floor(Math.random() * 10000000000);
      const fileExt = file.name.split(".").pop() || "jpg";
      const formattedFilename = `onboarding/${folderName}/image_cropper_${randomNumber}.${fileExt}`;

      const presignedReqData = {
        multiple_files: [
          {
            filename: formattedFilename,
            file_type: file.type || "image/jpeg",
          },
        ],
      };

      const presignedRes = await axios.post(PresentUrl, presignedReqData, { headers });
      const uploadedUrls = presignedRes.data?.data || presignedRes.data;

      let s3CleanUrl = "";
      if (Array.isArray(uploadedUrls) && uploadedUrls.length > 0) {
        const presignedPutUrl = uploadedUrls[0];
        s3CleanUrl = String(presignedPutUrl).split("?")[0];

        await fetch(presignedPutUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "image/jpeg",
            "x-amz-acl": "public-read",
          },
          body: file,
        });
      }

      const finalS3Url = s3CleanUrl || localUrl;

      dispatch(
        setIdImage({
          name,
          image: {
            name: file.name,
            url: finalS3Url,
            s3Url: finalS3Url,
            file: file,
          },
        })
      );

      if (name === "pan") {
        updateField("pan_id", finalS3Url);
      }

      // 2. OCR Detail Fetch API call to prefill PAN Number, DOB and Name
      if (name === "pan") {
        try {
          const formData = new FormData();
          formData.append("aadhar_card", file);
          const ocrRes = await axios.post(denaveAadharFetchApi, formData, { headers });
          const extracted =
            ocrRes.data?.data?.extracted_data || ocrRes.data?.extracted_data || ocrRes.data?.data || ocrRes.data;

          if (extracted) {
            const panNum =
              extracted.pan_no ||
              extracted.pan_number ||
              extracted.pan ||
              extracted.id_number ||
              extracted.number ||
              "";

            const panDob = extracted.dob || extracted.date_of_birth || "";
            const panName = extracted.name || extracted.name_as_per_pan || "";

            if (panNum) {
              const formattedPan = String(panNum).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
              updateField("pan_number", formattedPan);
              setIsPanVerified(false);
            }

            if (panDob) {
              setExtractedPanDob(panDob);
            }
            if (panName) {
              setExtractedPanName(panName);
            }
          }
        } catch (ocrErr) {
          console.error("PAN OCR detail fetch error:", ocrErr);
        }
      }
    } catch (err) {
      console.error("Presigned URL upload error for PAN:", err);
    } finally {
      dispatch(getLoaderAction2(false));
    }
  };

  const removeImage = (name) => {
    if (idVerificationImages[name]?.url) {
      URL.revokeObjectURL(idVerificationImages[name].url);
    }

    dispatch(removeIdImage(name));

    if (name === "pan") {
      updateField("pan_id", "");
      updateField("pan", "");
      updateField("pan_number", ""); // Clear prefilled PAN number on remove image
      setExtractedPanDob("");
      setExtractedPanName("");
      setIsPanVerified(false);
    }
  };

  const getMissingFields = useCallback(() => {
    const missing = [];
    const missingNames = new Set();

    // Aadhaar Number from Basic Information
    const aadharNumber =
      basicInformation.aadhar_number || basicInformation.aadhaar_number || "";

    if (String(aadharNumber).replace(/\D/g, "").length !== 12) {
      missing.push("Aadhaar Number");
      missingNames.add("aadhar_number");
    }

    // Aadhaar Front from Basic Information
    if (!basicImages.aadhaarFront) {
      missing.push("Aadhaar Front Image");
      missingNames.add("aadharfront_id");
    }

    // Aadhaar Back from Address Details
    if (!addressImages.aadhaarBack) {
      missing.push("Aadhaar Back Image");
      missingNames.add("aadharback_id");
    }

    // PAN Number validation (10 alphanumeric chars)
    const panVal = String(idVerification.pan_number || "").trim().toUpperCase();
    if (!panVal || panVal.length !== 10) {
      missing.push("Valid 10-character PAN Number");
      missingNames.add("pan_number");
    }

    const details = selfonboardData || employeeDetails;
    const idFormId = extractIdFormId(details);

    const isPanDone = Boolean(
      isPanVerified ||
      idVerification.pan_verified ||
      idVerification.is_pan_verified
    );

    if (!isPanDone) {
      missing.push("PAN Verification (Click Verify button)");
      missingNames.add("pan_number");
    }

    // PAN Image validation
    if (!idVerificationImages.pan && !idVerification.pan_id) {
      missing.push("PAN Card Image");
      missingNames.add("pan");
    }

    setErrorFields(missingNames);

    return missing;
  }, [
    basicInformation,
    basicImages,
    addressImages,
    idVerification,
    idVerificationImages,
    isPanVerified,
    selfonboardData,
    employeeDetails,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields]
  );

  const renderUpload = (name, label) => {
    const image = idVerificationImages[name] || addressImages[name] || basicImages[name];
    const hasError = errorFields.has(name);

    const getImageSrc = (img) => {
      if (!img) return "";
      if (typeof img === "string") return img;
      if (typeof img.url === "object" && img.url !== null) {
        return img.url.media_url || img.url.url || "";
      }
      return img.url || img.s3Url || "";
    };

    const imgSrc = getImageSrc(image);

    return (
      <div className={styles.uploadBlock}>
        <span>
          {label}
          <b>*</b>
        </span>

        <div className={styles.previewRow}>
          {imgSrc ? (
            <>
              <img
                className={styles.imagePreview}
                src={imgSrc}
                alt={`${label} preview`}
              />
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => removeImage(name)}
              >
                Remove Image
              </button>
            </>
          ) : idVerification.pan_id && name === "pan" ? (
            <>
              <img
                className={styles.imagePreview}
                src={idVerification.pan_id}
                alt="PAN preview"
              />
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => removeImage(name)}
              >
                Remove Image
              </button>
            </>
          ) : (
            <label className={styles.uploadButton}>
              Upload Image
              <input
                className={styles.hiddenInput}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={(event) =>
                  handleFileInputChange(name, event)
                }
              />
            </label>
          )}
        </div>

        {hasError && (
          <p className={styles.fieldError}>Please upload this image</p>
        )}
      </div>
    );
  };

  const aadharNumber =
    basicInformation.aadhar_number || basicInformation.aadhaar_number || "";

  return (
    <div className={styles.idVerification}>
      <header className={styles.sectionHeader}>ID Verification</header>

      <div className={styles.content}>
        <div className={styles.grid}>
          <label className={styles.field}>
            <span>
              Aadhaar Card Number<b>*</b>
            </span>
            <input
              value={
                aadharNumber
                  ? `**** **** ${String(aadharNumber).slice(-4)}`
                  : ""
              }
              disabled
              className={
                errorFields.has("aadhar_number") ? styles.inputError : ""
              }
            />
            {errorFields.has("aadhar_number") && (
              <p className={styles.fieldError}>
                Please enter a valid 12-digit Aadhaar number in Basic
                Information
              </p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              PAN Card Number<b>*</b>
            </span>
            <div className={styles.inputWithAction}>
              <input
                value={idVerification.pan_number || ""}
                maxLength={10}
                className={errorFields.has("pan_number") ? styles.inputError : ""}
                onChange={(event) => {
                  const val = event.target.value.toUpperCase().slice(0, 10);
                  updateField("pan_number", val);
                  if (isPanVerified) {
                    setIsPanVerified(false);
                  }
                }}
              />
              {isPanVerified ? (
                <span className={styles.verifiedBadge} style={{ display: "flex" }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#047857"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Verified
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.verifyBtn}
                  disabled={
                    String(idVerification.pan_number || "").trim().length !== 10 || isVerifyingPan
                  }
                  onClick={handleVerifyPan}
                >
                  {isVerifyingPan ? "Verifying..." : "Verify"}
                </button>
              )}
            </div>
            {errorFields.has("pan_number") && (
              <p className={styles.fieldError}>Please enter a valid 10-character PAN number</p>
            )}
          </label>

          {/* <div className={styles.uploadBlock}>
            <span>
              Aadhaar Front<b>*</b>
            </span>
            {basicImages.aadhaarFront ? (
              <img
                className={styles.imagePreview}
                src={basicImages.aadhaarFront.url}
                alt="Aadhaar Front"
              />
            ) : (
              <p className={styles.fieldError}>
                Please upload Aadhaar Front in Basic Information
              </p>
            )}
          </div> */}

          {/* <div className={styles.uploadBlock}>
            <span>
              Aadhaar Back<b>*</b>
            </span>
            {addressImages.aadhaarBack ? (
              <img
                className={styles.imagePreview}
                src={addressImages.aadhaarBack.url}
                alt="Aadhaar Back"
              />
            ) : (
              <p className={styles.fieldError}>
                Please upload Aadhaar Back in Address Details
              </p>
            )}
          </div> */}

          {renderUpload("pan", "Photo of PAN Card")}
        </div>
      </div>

      {cropperOpen && selectedImageEvent && (
        <ImageCropper
          updateAvatar={() => { }}
          selectedImage={selectedImageEvent}
          getCroppedImg={getCroppedImg}
          closeModal={() => setCropperOpen(false)}
        />
      )}
    </div>
  );
});

export default IdVerification;
